<template>
  <v-dialog :value="value" max-width="900" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="font-weight-bold">
        {{ title }}
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <div class="px-4 pt-4 pb-2">
        <v-text-field
          ref="searchField"
          v-model="search"
          outlined
          dense
          hide-details
          placeholder="Search by name, email, or phone"
          prepend-inner-icon="mdi-magnify"
          clearable
        />
      </div>
      <v-card-text class="pa-0">
        <UserDataTable
          :selected="[]"
          :columns="columns"
          :users="filteredUsers"
          :options="table.options"
          @update:options="table.options = $event"
          :total="table.total"
          :itemsPerPage="table.itemsPerPage"
          :footerProps="table.footerProps"
          :loading="table.loading"
          :search="search"
          :clickableRows="true"
          :external="false"
          @row-click="handleSelect"
          @checkbox-click="handleSelect"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import UserDataTable from "@/components/people/UserDataTable.vue";
import UserService from "@/services/UserService.js";
import { mapState } from "vuex";

export default {
  name: "UserSelectDialog",
  components: { UserDataTable },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Select User",
    },
    excludeUserId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ["input", "select"],
  data() {
    return {
      search: null,
      table: {
        users: [],
        total: 0,
        loading: false,
        options: {},
        itemsPerPage: 10,
        footerProps: { "items-per-page-options": [10, 25, 50] },
      },
      columns: [
        { text: " ", align: "center", sortable: false, value: "profilePicUrl", width: "20px" },
        { text: "Name / Email or Phone", value: "tableDisplayName", width: "50%" },
        { text: "Organization / Job Title", value: "organization", width: "50%" },
      ],
    };
  },
  computed: {
    ...mapState(["globalClientId"]),
    filteredUsers() {
      if (!this.excludeUserId) return this.table.users;
      return this.table.users.filter((u) => u.userId !== this.excludeUserId);
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.fetchUsers();
        setTimeout(() => this.$refs.searchField?.focus(), 300);
      } else {
        this.search = null;
        this.table.options = { ...this.table.options, page: 1 };
      }
    },
    "table.options": {
      handler() {
        if (this.value) this.fetchUsers();
      },
      deep: true,
    },
    search() {
      if (!this.value) return;
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.table.options = { ...this.table.options, page: 1 };
        this.fetchUsers();
      }, 300);
    },
    globalClientId() {
      this.table.users = [];
      this.table.options = {};
    },
  },
  beforeUnmount() {
    clearTimeout(this._searchTimer);
  },
  methods: {
    handleSelect(user) {
      this.$emit("select", user);
      this.$emit("input", false);
    },
    async fetchUsers() {
      try {
        this.table.loading = true;
        const options = this.table.options;
        const limit = options.itemsPerPage || this.table.itemsPerPage;
        const page = options.page || 1;
        const offset = (page - 1) * limit;

        let sort = "User.firstName ASC, User.lastName ASC";
        if (options.sortBy?.length > 0) {
          const col = options.sortBy[0];
          const dir = options.sortDesc?.[0] ? "DESC" : "ASC";
          sort =
            col === "tableDisplayName"
              ? `User.firstName ${dir}, User.lastName ${dir}`
              : `${col} ${dir}`;
        }
        const response = await UserService.getUsersV2({
          limit,
          offset,
          sort,
          clientId: this.globalClientId,
          screen: "people",
          search: this.search || undefined,
        });

        this.table.users = response.result.rows.map((user) => ({
          ...user,
          tableDisplayName: user.firstName + " " + user.lastName,
        }));
        this.table.total = response.result.count;
      } catch (err) {
        console.log("Error fetching users", err);
      } finally {
        this.table.loading = false;
      }
    },
  },
};
</script>
